import { t } from '@/utils/i18n'
import type { CloudProduct, UnitPriceItem, ProductItem } from '@/types/product'
import constant from '@/constant'
import { utils, cal } from '@/utils'
import { JSONStringify } from 'lib/tool'

const { randomn, isArray, px2rem, cloneDeep } = utils
const sortOptions = constant.product.getSortOptions()
/**
 * @desc 获取商品推荐
 * @param {Object}  cloudProduct 商品对象
 * @return {String} 推荐词（新、爆、促）
 */
function getRecommendTag(cloudProduct: CloudProduct) {
  let tag = ''
  if (cloudProduct && cloudProduct.recommend === '1') {
    switch (cloudProduct.recommendType) {
      case 1:
        tag = t('新')
        break
      case 2:
        tag = t('爆')
        break
      case 4:
        tag = t('促')
        break
      default:
        tag = t('荐')
        break
    }
  }

  return tag
}
/**
 * @desc 获取商品推荐样式
 * @param {Object}  cloudProduct 商品对象
 * @return {String} 推荐样式
 */
function getRecommendClass(cloudProduct: CloudProduct) {
  let className = 'hidden'
  if (cloudProduct && cloudProduct.recommend === '1') {
    switch (cloudProduct.recommendType) {
      case 1:
        className = 'recommend recommend-1'
        break
      case 2:
        className = 'recommend recommend-2'
        break
      case 4:
        className = 'recommend recommend-4'
        break
      default:
        className = 'recommend'
        break
    }
  }

  return className
}

function getFontSize(text: string | number) {
  const textLength = text.toString().length

  if (textLength >= 10) {
    return px2rem(18)
  }

  if (textLength >= 9) {
    return px2rem(20)
  }

  if (textLength >= 8) {
    return px2rem(22)
  }

  if (textLength >= 7) {
    return px2rem(24)
  }

  if (textLength >= 6) {
    return px2rem(26)
  }

  if (textLength >= 4) {
    return px2rem(28)
  }

  return px2rem(30)
}

/**
 * @desc 获取商品起订量(转化为当前单位)
 * @param {Object}  cloudProduct 商品对象
 * @return {Number} 起订量
 */
function getStartQuantityWithUnit(
  cloudProduct: CloudProduct,
  unitIndex: number,
  precision: number
) {
  const { unitPriceArray, productMoqUnitIndex } = cloudProduct
  let startQuantity = getStartQuantity(cloudProduct)

  if (
    typeof cloudProduct.productMoqQty === 'number' &&
    unitPriceArray &&
    unitPriceArray[productMoqUnitIndex] &&
    unitPriceArray[unitIndex]
  ) {
    const moqUnit = unitPriceArray[productMoqUnitIndex]
    const currUnit = unitPriceArray[unitIndex]

    startQuantity = getRoundUpQuantityByPrecision({
      val:
        (Number(cloudProduct.productMoqQty) * Number(moqUnit.unitRatio)) /
        Number(currUnit.unitRatio),
      precision,
    })
  }

  return startQuantity
}

/**
 * @desc 获取商品起订量
 * @param {Object}  cloudProduct 商品对象
 * @return {number} 起订量
 */
function getStartQuantity(cloudProduct: CloudProduct) {
  let startQuantity = 0

  if (typeof cloudProduct.productMoqQty === 'number') {
    startQuantity = cloudProduct.productMoqQty
  }

  return startQuantity
}

/**
 * 根据精度返回处理后数量(向上取整) 主要用于起订量 因为起订量不应向下取整
 *
 * @param {object} state
 * @param {number} val
 * @param {string} precision
 * @return {number | string}
 */
function getRoundUpQuantityByPrecision({ ...e }) {
  let str = ''

  if (e.val < 0) {
    e.val = -e.val
    str = '-'
  } else {
    str = ''
  }

  if (e.val.toString().indexOf('.') === -1) {
    if (str === '-') {
      return -e.val
    }
    return e.val
  }

  const value = e.val.toString()
  const integer = value.substr(0, value.indexOf('.'))
  const decimal = value.substr(value.indexOf('.') + 1)
  let newDecimal = ''
  const precision = e.precision || 0

  if (decimal.length > precision) {
    newDecimal = decimal.substr(0, precision)

    if (str === '-') {
      return `-${integer}.${newDecimal}`
    }

    return `${integer}.${newDecimal}`
  }

  if (str === '-') {
    return `-${integer}.${decimal}`
  }

  return `${integer}.${decimal}`
}

/**
 * 设置产品价格和划线价
 *
 * @param {*} { chooseUnit 选择的单位对象, isMultiPrice 是否为不同规格不同价格, showPriceRange = true  是否显示价格范围}
 */
function setProductPrice(
  chooseUnit: UnitPriceItem,
  isMultiPrice: boolean,
  showPriceRange: boolean = true,
  pricePrecision: number = 2
) {
  const formatPrice = (price: number | string) => cal.moneyFormat(Number(price), pricePrecision);

  if (!isMultiPrice) {
    chooseUnit.priceText = formatPrice(chooseUnit.price)
    chooseUnit.originPriceText =
      chooseUnit.linePrice > chooseUnit.price ? formatPrice(chooseUnit.linePrice) : ''
    // 计算优惠了多少折扣
    if (chooseUnit.linePrice > chooseUnit.price) {
      chooseUnit.discount = cal.getDown(2,
        cal.divide(
          chooseUnit.linePrice - chooseUnit.price,
          chooseUnit.linePrice
        )
      )

      chooseUnit.discount = cal.times(chooseUnit.discount, 100) + '%'
    }
    return
  }

  const { minPrice, maxPrice, linePrice, maxLinePrice } = chooseUnit

  // 如果是不同规格不同价格
  if (!showPriceRange || minPrice === maxPrice) {
    chooseUnit.priceText = formatPrice(Number(minPrice))
  } else {
    chooseUnit.priceText = `${formatPrice(Number(minPrice))}~${formatPrice(Number(maxPrice))}`
  }

  // 有上次价时，显示为最小上次价
  if (chooseUnit.isLastPrice) {
    chooseUnit.priceText = formatPrice(Number(chooseUnit.minLastPrice))
  }

  // 划线价
  if (showPriceRange) {
    chooseUnit.originPriceText =
      linePrice === maxLinePrice ? formatPrice(linePrice) : `${formatPrice(linePrice)}~${formatPrice(Number(maxLinePrice))}`
  } else {
    chooseUnit.originPriceText = formatPrice(linePrice)
  }

  const minDiscount = cal.getDown(2, cal.divide(cal.minus(Number(linePrice), Number(minPrice)), Number(linePrice)))
  const maxDiscount = cal.getDown(2, cal.divide(cal.minus(Number(maxLinePrice), Number(maxPrice)),Number(maxLinePrice)))

  chooseUnit.discount = `${cal.times(minDiscount as number, 100)}%~${cal.times(maxDiscount as number, 100)}%`

  // 当划线价小于或等于最小sku报价时，不显示划线价
  if (
    Number(linePrice) <= Number(maxPrice) ||
    Number(maxLinePrice) <= Number(maxPrice)
  ) {
    chooseUnit.originPriceText = ''
    chooseUnit.discount = ''
  }
}

/**
 * @desc 多规格格式化作为key
 * @param {Object}  elements 商品规格对象
 * @return {String} 规格
 */
const encodeSpecs = (elements: any) => {
  const prefix = 'SPECS'
  const elementIdJoin = (elements || [])
    .map((item: any) => item.elementId)
    .join('-')

  return `${prefix}-${elementIdJoin}`
}

/**
 * @description 修改销售货品单位信息(利用了引用类型特性,不需要return)
 * @param {product} 货品实体,包括cloudProduct和saleProduct
 * @param {unitIndex} 要修改的单位索引（需传入起订单位索引）
 * @param {number} 要修改的单位索引
 */
function editSaleUnit(product: ProductItem, index: number) {
  const { cloudProduct, saleProduct = {} } = product
  const { unitPriceArray } = cloudProduct
  // 修改默认单位索引
  cloudProduct.defaultIndex = index
  // 修改下单货品单位索引及信息
  if (unitPriceArray && unitPriceArray.length > 0) {
    const chooseUnit = unitPriceArray[index]

    ;(saleProduct?.details || []).forEach((it: any) => {
      it.price = unitPriceArray[0].originPrice
      it.priceUnitIndex = 0
      it.priceUnitName = unitPriceArray[0].unitName
      it.priceUnitRatio = unitPriceArray[0].unitRatio
      it.selectPrice = unitPriceArray[0].price
      it.unitIndex = index
      it.unitName = chooseUnit.unitName
      it.unitRatio = chooseUnit.unitRatio
    })
  }
}

/**
 * @description 修改货品销售数量(利用了引用类型特性,不需要return)
 * @param {product} product 货品实体,包括cloudProduct和saleProduct
 * @param {number} quantity 货品数量
 */
function editSaleQuantity(product: ProductItem, quantity: number) {
  const { cloudProduct, saleProduct = {} } = product
  const { unitPriceArray } = cloudProduct
  let unitIndex = cloudProduct.defaultIndex

  // 如果已加购则直接修改数量
  if (product.saleProduct && saleProduct.details) {
    saleProduct.details[0].quantity = quantity
    return
  }

  // 未加购时
  if (saleProduct && saleProduct.details && saleProduct.details.length) {
    // 如果已加购则取加购里的单位索引
    unitIndex = saleProduct.details[0].unitIndex
  }

  // 当前单位
  if (unitPriceArray && unitPriceArray.length > 0) {
    const chooseUnit = unitPriceArray[unitIndex]
    const details = [
      {
        elements: [],
        price: unitPriceArray[0].originPrice,
        priceUnitIndex: unitPriceArray[0].unitIndex,
        priceUnitName: unitPriceArray[0].unitName,
        priceUnitRatio: unitPriceArray[0].unitRatio,
        selectPrice: unitPriceArray[0].price,
        priceTypeId: chooseUnit.priceTypeId,
        priceIndex: chooseUnit.priceIndex,
        quantity,
        saleDetailId: null,
        unitIndex,
        unitName: chooseUnit.unitName,
        unitRatio: chooseUnit.unitRatio,
      },
    ]

    product.saleProduct = {
      details,
      productId: cloudProduct.productId,
    }

    return product
  }
}

/**
 * @desc 给搜索结果添加高亮
 * @param {Array}  商品列表
 * @return {Array} 商品列表
 */
function highlightList(list: any[], query: string) {
  const keywords = query.toLowerCase().split(/\s+/)
  const regexp = new RegExp(`(${keywords.join('|')})`)
  return list.map((p) => {
    const product = p.cloudProduct
    const title = product.productName

    p.key = Date.now() + randomn(4)
    if (regexp.test(title)) {
      const segments = title.split(regexp)
      const children = []

      if (product.recommend === 1) {
        children.push({
          name: 'span',
          attrs: { class: getRecommendClass(product) },
          children: [{ text: getRecommendTag(product), type: 'text' }],
        })
      }
      segments.forEach((text: string) => {
        const cssClass = regexp.test(text) ? 'highlight' : ''
        const node = {
          name: 'span',
          attrs: { class: cssClass },
          children: [{ text, type: 'text' }],
        }
        children.push(node)
      })

      product.highlight = [
        {
          name: 'div',
          attrs: { class: 'search-highlight-text' },
          children,
        },
      ]
    } else {
      const obj = {
        name: 'div',
        children: [
          {
            name: 'span',
            attrs: { class: getRecommendClass(product) },
            children: [{ text: getRecommendTag(product), type: 'text' }],
          },
          {
            text: `${title}`,
            attrs: { class: 'search-highlight-text' },
            type: 'text',
          },
        ],
      }
      product.highlight = [obj]
    }
    return p
  })
}

// 如果有视频就去掉第一个
function mediaPop(mediaList: any[], hasVideo: boolean) {
  if (hasVideo) {
    mediaList.shift()
    return mediaList
  }
  return mediaList
}

// 解析装箱规格
export const getSpecification = (unitPriceArray: UnitPriceItem[]) => {
  let specification = ''
  if (isArray(unitPriceArray)) {
    if (unitPriceArray.length === 1) {
      specification = unitPriceArray[0].unitName || ''
      return specification
    } else {
      for (let i = unitPriceArray.length - 1; i >= 0; i--) {
        const unitItem = unitPriceArray[i]
        if (i === unitPriceArray.length - 1) {
          specification += `1 ${unitItem.unitName} * `
        } else {
          specification += `${cal.divide(unitPriceArray[i + 1].unitRatio, unitItem.unitRatio)} ${unitItem.unitName} * `
        }
      }

      return specification.substr(0, specification.length - 2)
    }
  } else {
    console.warn('getSpecification方法只接收数组对象')
    return ''
  }
}

/**
 * @desc 构造排序列表
 */
function getSortDist(salesOn: boolean, salesSlipOn: boolean) {
  return sortOptions.filter((option) => {
    const { key } = option;
    // 如果未打开销量开关，不显示“按销量排序”
    if (key === '2' && !salesOn) {
      return false;
    }

    if (key === '4') {
      // 如果打开了销量开关或未打开销售单开关，不显示“按销售单排序”（注意要先判断销量开关是否打开）
      if (salesOn || !salesSlipOn) {
        return false;
      }
    }

    // 其它情况不过滤
    return true;
  });
}

/**
 * 初始化排序选项
 *
 */
function initSortOptions(cloudProductSort: number, salesOn: boolean, salesSlipOn: boolean) {
  const sortOptions = getSortDist(salesOn, salesSlipOn)

  let activeIndex
  let sortDirection
  let findResult

  sortOptions.forEach((item, index) => {
    if (item.cloudProductSort.includes(cloudProductSort)) {
      findResult = item
      activeIndex = index
    }
  })

  if (!findResult) {
    [findResult] = sortOptions
    activeIndex = 0
  }

  const activeKey = findResult.key
  const sortString = findResult.name

  if ([2, 4, 6].includes(cloudProductSort)) {
    sortDirection = 'DESC'
  } else if ([3, 5].includes(cloudProductSort)) {
    sortDirection = 'ASC'
  } else {
    sortDirection = findResult.sortDirection
  }

  const sortActive = {
    activeIndex,
    activeKey,
    sortDirection,
    sortString,
  }

  return {
    sortOptions,
    sortActive,
  }
}

/**
 * 格式化价格
 *
 * @param {number} price
 * @returns
 */
function formatPrice(price: number, pricePrecision: number = 2) {
  if (price === null || price === void 0) {
    return price
  }

  return cal.round(price, pricePrecision)
}

/**
 * 根据当前单位，获取最小单位的数量，例子：格式是1件（10个），1箱（100个）
 * @param unitIndex 
 * @param product 
 * @returns 
 */
const getMinUnits = (unitIndex: number, product: ProductItem, quantity: number = 1) => {
  const unitList = product.cloudProduct?.unitPriceArray || [];
  const len = unitList.length;
  if (len <= 1 || unitIndex === 0) {
    return '';
  }

  const unit = unitList[unitIndex];
  const minUnit = unitList[0] || {};
  return ` (${quantity*unit.unitRatio}${minUnit.unitName})`;
};

/**
 * 根据默认单位索引设置单位对象
 * @param product 货品对象
 * @param isMultiPrice 是否多价格
 * @param cloudStoreStockType 是否开启显示可售库存
 * @param qualityPrecision 数量小数位
 * @param pricePrecision 金额小数位
 * @returns 
 */
function setChooseUnit(product: ProductItem, isMultiPrice: boolean = false, cloudStoreStockType: number, qualityPrecision: number = 2, pricePrecision: number = 2) {
  const { cloudProduct, saleProduct } = product;
  const { productStock, productLockStock, productMoqQty } = cloudProduct;
  const unitPriceArray = cloudProduct.unitPriceArray || [];
  let index = cloudProduct.productMoqUnitIndex; // 起订量单位索引

  // 如果已加购则取加购里的单位索引
  if (saleProduct && saleProduct.details && saleProduct.details.length) {
    index = saleProduct.details[0].unitIndex;
  }

  // 如果已加购且单位被删除则可能取不到单位,此时拿第0个单位(跟购物车下单货品的单位展示会不一致,但超级采购+之前就是这么做的)
  const currentChooseUnit = cloneDeep(unitPriceArray[index]) || unitPriceArray[0];

  // 价格、价格单位、价格索引取基础单位
  Object.assign(currentChooseUnit, {
    unitIndex: index, // 当前单位索引
    priceText: unitPriceArray[0].priceText,
    price: unitPriceArray[0].price,
    originPrice: unitPriceArray[0].originPrice,
    linePrice: unitPriceArray[0].linePrice,
    maxLastPrice: unitPriceArray[0].maxLastPrice,
    maxLinePrice: unitPriceArray[0].maxLinePrice,
    maxPrice: unitPriceArray[0].maxPrice,
    minLastPrice: unitPriceArray[0].minLastPrice,
    minPrice: unitPriceArray[0].minPrice,
    priceUnitRatio: unitPriceArray[0].unitRatio,
    priceUnitIndex: 0,
    priceUnitName: unitPriceArray[0].unitName,
  })

  currentChooseUnit.stock = productStock;
  // 如果云店设置显示的是可售库存，则可售库存=库存-锁定库存
  if (cloudStoreStockType === 1 && productLockStock) {
    currentChooseUnit.stock = cal.minus(currentChooseUnit.stock, productLockStock);
  }

  /**
   * 单位下总库存（向下取整）
   * prod.productStock 基础单位库存
   * chooseUnit.unitRatio 单位比例
   * 解释：基础单位的意思是商品的最小单位。比如卖矿泉水，最小是一瓶，基础单位是瓶。那么“瓶”的单位比例是1，一箱12瓶，“箱”的单位比例是12。假设基础单位库存24瓶，即prod.productStock为24，那么当客户选择瓶的时候，24/1，显示的库存是24瓶，当客户选择箱的试试，24/12，显示的库存是2箱
   */
  currentChooseUnit.stock = cal.getDown(
    qualityPrecision,
    cal.divide(currentChooseUnit.stock, currentChooseUnit.unitRatio)
  ) as number;

  // 起订量
  currentChooseUnit.productMoqQty = productMoqQty;

  // 单位下销量
  currentChooseUnit.saleNum = cal.round(
    cal.divide(cloudProduct.saleNum || 0, currentChooseUnit.unitRatio),
    qualityPrecision
  );

  setProductPrice(currentChooseUnit, isMultiPrice, true, pricePrecision);
  return currentChooseUnit;
}

export default {
  getRecommendTag,
  getRecommendClass,
  getFontSize,
  getStartQuantityWithUnit,
  getStartQuantity,
  setProductPrice,
  encodeSpecs,
  editSaleUnit,
  editSaleQuantity,
  highlightList,
  mediaPop,
  getSpecification,
  getSortDist,
  initSortOptions,
  formatPrice,
  getMinUnits,
  setChooseUnit,
}
